"use client";

import { useEffect, useRef, useState } from "react";

const mono = "'JetBrains Mono',monospace";
const BLUE = "#2C5CFF";

// 첫 화면에 보여줄 예시 질문 (클릭 시 바로 전송)
const EXAMPLE_QUESTIONS = [
  "지원 대상과 자격 조건이 어떻게 되나요?",
  "신청 방법과 접수 기간을 알려주세요",
];

interface Msg {
  role: "user" | "assistant";
  text: string;
}

// 어시스턴트 답변의 링크를 새 창으로 열리는 클릭 링크로 렌더 (HTML <a> / 마크다운 / 맨 URL 지원).
//   위험 태그(script/style/on*)는 제거하고, 모든 <a> 에 target=_blank rel 강제.
function toSafeHtml(raw: string): string {
  let s = raw
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, ""); // 인라인 이벤트 핸들러 제거
  // 마크다운 링크 [텍스트](url) → <a>
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2">$1</a>');
  // 맨 URL(이미 href/앵커 안이 아닌 것) → <a>
  s = s.replace(/(^|[^"'>=\]])(https?:\/\/[^\s<)]+)/g, '$1<a href="$2">$2</a>');
  // 모든 <a> 에 target=_blank + rel 강제 (기존 target/rel 은 제거 후 재설정)
  s = s.replace(/<a\s+([^>]*?)>/gi, (_m, attrs: string) => {
    const cleaned = attrs
      .replace(/\s*target\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
      .replace(/\s*rel\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
    return `<a ${cleaned.trim()} target="_blank" rel="noopener noreferrer">`;
  });
  return s;
}

// 공고문 챗봇 — /programs/[id]. 서버는 Google ADK(vster_agent).
//   variant="floating"(기본): 우하단 플로팅 위젯.
//   variant="inline": PDF 뷰어 옆에 나란히 붙는 고정 패널(항상 열림, 토글 없음).
export default function ProgramChatbot({
  docId,
  title,
  variant = "floating",
}: {
  docId: number;
  title: string;
  variant?: "floating" | "inline";
}) {
  const inline = variant === "inline";
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([
    { role: "assistant", text: "이 공고문에 대해 궁금한 점을 물어보세요. (지원대상·지원내용·신청방법·접수기간 등)" },
  ]);
  const sessionId = useRef<string>("");
  const sessionReady = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 마운트 시 1회 세션 ID 생성 (대화 히스토리 유지)
    sessionId.current = `doc${docId}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
    sessionReady.current = false;
  }, [docId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, open, busy]);

  // Google ADK(vster_agent) 를 nginx /adk 경유로 직접 호출.
  const ADK = "/adk";
  const APP = "vster_agent";
  const UID = "web";

  async function send(preset?: string) {
    const q = (preset ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      // 1) 세션 보장 (state.doc_id) — 최초 1회.
      //    create_session_with_id 는 body 를 'state dict 그대로' 받는다 → { doc_id } (X: { state:{...} })
      if (!sessionReady.current) {
        await fetch(`${ADK}/apps/${APP}/users/${UID}/sessions/${encodeURIComponent(sessionId.current)}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ doc_id: String(docId) }),
        });
        sessionReady.current = true;
      }
      // 2) 실행
      const r = await fetch(`${ADK}/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          app_name: APP,
          user_id: UID,
          session_id: sessionId.current,
          new_message: { role: "user", parts: [{ text: q }] },
        }),
      });
      if (!r.ok) {
        setMsgs((m) => [...m, { role: "assistant", text: `오류가 발생했습니다. (${r.status})` }]);
        return;
      }
      // 3) 이벤트 배열에서 모델의 마지막 텍스트(최종 답변) 추출
      const events = (await r.json()) as Array<{ content?: { parts?: Array<{ text?: string }> } }>;
      let answer = "";
      if (Array.isArray(events)) {
        for (const e of events) {
          for (const p of e?.content?.parts ?? []) {
            if (typeof p?.text === "string" && p.text.trim()) answer = p.text;
          }
        }
      }
      setMsgs((m) => [...m, { role: "assistant", text: answer.trim() || "답변을 생성하지 못했습니다." }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", text: "챗봇 서버에 연결할 수 없습니다." }]);
    } finally {
      setBusy(false);
    }
  }

  // 채팅 패널 (floating/inline 공용). inline 은 컨테이너를 꽉 채우고 뷰어 높이(80vh)에 맞춤.
  const panel = (
    <div
      style={{
        width: inline ? "100%" : "min(380px, calc(100vw - 32px))",
        height: inline ? "100%" : "min(560px, calc(100vh - 120px))", // inline: 부모 grid 높이에 맞춤
        minHeight: 0,
        background: "#fff",
        border: "1px solid #e4e8f6",
        borderRadius: inline ? 14 : 16,
        boxShadow: inline ? undefined : "0 12px 40px rgba(5,26,62,.18)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        marginBottom: inline ? 0 : 12,
        animation: "vfade .2s ease",
      }}
    >
      {/* 헤더 */}
      <div style={{ background: BLUE, color: "#fff", padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>공고문 챗봇</div>
          <div style={{ fontFamily: mono, fontSize: 11, opacity: 0.85, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 280 }}>{title}</div>
        </div>
        {!inline && (
          <button onClick={() => setOpen(false)} aria-label="닫기" style={{ background: "transparent", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", lineHeight: 1, padding: 4 }}>×</button>
        )}
      </div>

      {/* 메시지 목록 */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10, background: "#F4F6FA" }}>
        <style>{`.chat-msg a{color:${BLUE};text-decoration:underline;word-break:break-all}.chat-msg a:hover{opacity:.8}`}</style>
        {msgs.map((m, i) => {
          const bubbleStyle: React.CSSProperties = {
            padding: "9px 12px",
            borderRadius: 12,
            fontSize: 14,
            lineHeight: 1.55,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            background: m.role === "user" ? BLUE : "#fff",
            color: m.role === "user" ? "#fff" : "#222538",
            border: m.role === "user" ? "none" : "1px solid #e4e8f6",
          };
          return (
            <div
              key={i}
              style={
                m.role === "user"
                  ? { alignSelf: "flex-end", maxWidth: "85%" }
                  : { alignSelf: "stretch", maxWidth: "100%" } // 어시스턴트 답변은 폭 꽉 채워 좌우 여백 동일
              }
            >
              {m.role === "assistant" ? (
                // 링크 클릭(새 창) 가능하도록 HTML 렌더
                <div className="chat-msg" style={bubbleStyle} dangerouslySetInnerHTML={{ __html: toSafeHtml(m.text) }} />
              ) : (
                <div style={bubbleStyle}>{m.text}</div>
              )}
            </div>
          );
        })}
        {/* 예시 질문 — 첫 화면(인사말만)일 때, 클릭 시 바로 전송 */}
        {msgs.length === 1 && !busy && (
          <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 2 }}>
            {EXAMPLE_QUESTIONS.map((qz) => (
              <button
                key={qz}
                type="button"
                onClick={() => send(qz)}
                style={{ alignSelf: "flex-start", maxWidth: "85%", textAlign: "left", background: "#fff", border: `1px solid ${BLUE}`, color: BLUE, borderRadius: 12, padding: "9px 12px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", lineHeight: 1.4 }}
              >
                {qz}
              </button>
            ))}
          </div>
        )}
        {busy && (
          <div style={{ alignSelf: "flex-start", fontFamily: mono, fontSize: 12, color: "#737685", padding: "4px 8px" }}>답변 작성 중…</div>
        )}
      </div>

      {/* 입력 */}
      <div style={{ display: "flex", gap: 8, padding: 12, borderTop: "1px solid #eef1fb", background: "#fff", flexShrink: 0 }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="질문을 입력하세요"
          disabled={busy}
          style={{ flex: 1, border: "1px solid #c3c6d6", borderRadius: 9, padding: "9px 11px", fontSize: 14, background: "#f1f3ff", color: "#0E1726", outline: "none", minWidth: 0 }}
        />
        <button onClick={() => send()} disabled={busy || !input.trim()} style={{ background: BLUE, color: "#fff", border: "none", borderRadius: 9, padding: "0 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: busy || !input.trim() ? 0.5 : 1, flexShrink: 0 }}>전송</button>
      </div>
    </div>
  );

  // 인라인: 패널만 그대로 반환 (부모 grid 컬럼에 배치).
  if (inline) return panel;

  // 플로팅: 우하단 고정 위젯 + 토글 버튼.
  return (
    <div style={{ position: "fixed", right: 24, bottom: 24, zIndex: 1000, fontFamily: "inherit" }}>
      {open && panel}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="공고문 챗봇 열기"
        style={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: BLUE,
          color: "#fff",
          border: "none",
          borderRadius: 999,
          padding: open ? "0" : "13px 18px",
          width: open ? 56 : "auto",
          height: 56,
          justifyContent: "center",
          boxShadow: "0 6px 20px rgba(0,61,155,.35)",
          cursor: "pointer",
          fontSize: 15,
          fontWeight: 600,
          float: "right",
        }}
      >
        {open ? <span style={{ fontSize: 22 }}>×</span> : <><span style={{ fontSize: 18 }}>💬</span> 공고문 챗봇</>}
      </button>
    </div>
  );
}
